import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const DEFAULT_STATS = [
  { title: "20+ Years", sub: "Building Brands" },
  { title: "Strategy to Application", sub: "One Creative Studio" },
  { title: "3D Visualization", sub: "In-House Expertise" },
];

const StatIcon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp className={className} strokeWidth={2} />;
};

const InlineLink = ({ to, children, testid, className }) => {
  // Internal routes use react-router Link; anchor / external URLs use plain <a>
  if (to?.startsWith("#") || to?.startsWith("http")) {
    return (
      <a
        href={to}
        data-testid={testid}
        onClick={(e) => {
          if (to.startsWith("#")) {
            e.preventDefault();
            const el = document.querySelector(to);
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }
        }}
        className={className}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={to || "/"} data-testid={testid} className={className}>
      {children}
    </Link>
  );
};

export const Hero = ({ settings = {} }) => {
  const imgDesktop = settings.hero_image_desktop || settings.hero_image || "";
  const imgMobile = settings.hero_image_mobile || settings.hero_image || imgDesktop;
  const imgAlt = settings.hero_image_alt || "Creative laptop with design software";
  // hero_eyebrow is rendered again (Sep 2026 final CTA pass).
  const eyebrow = settings.hero_eyebrow || "Brand Studio · Since 2005";
  const line1 = settings.hero_title_line1 || "Brands Built for";
  const line2 = settings.hero_title_line2 || "the Real World.";
  const description = settings.hero_description || "Branding, design, campaigns, packaging and 3D visualization — from complete brands to everyday design. Made in Aruba, for businesses anywhere.";
  const stats = (settings.hero_stats && settings.hero_stats.length > 0) ? settings.hero_stats : DEFAULT_STATS;
  const ctaPrimary = { label: settings.hero_cta_primary_label || "Start a Project", link: settings.hero_cta_primary_link || "/contact" };
  const ctaSecondary = { label: settings.hero_cta_secondary_label || "Order Design Online", link: settings.hero_cta_secondary_link || "/services#services-finder" };
  const heroAccentInline = !!settings.hero_accent_inline;
  const heroShowDot = !!settings.hero_show_dot;

  // Hero video: MP4 is required for the video path to activate. WebM is
  // optional — when present it's listed first in the <source> list so
  // browsers that decode it (Chrome / Firefox) pick it over MP4.
  const videoEnabled = !!settings.hero_video_enabled && !!settings.hero_video_mp4;
  const videoMp4 = settings.hero_video_mp4 || "";
  const videoWebm = settings.hero_video_webm || "";

  // Viewport gate — desktop composition kicks in at Tailwind's `lg:`
  // breakpoint (1024 px). Below that we render the mobile stacked
  // composition. Both branches now consume the SAME CMS video fields
  // (`hero_video_enabled`, `hero_video_mp4`, `hero_video_webm`) —
  // there is no separate mobile animation source. The old
  // desktop-only decision has been retired.
  // The lazy useState initializer reads matchMedia SYNCHRONOUSLY on
  // first render so the initial DOM already reflects the correct
  // viewport branch (no flash-mount of the desktop <video> at 390px
  // before the effect runs). `typeof window` guard keeps SSR safe.
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const apply = () => setIsDesktop(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener("change", apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", apply) : mq.removeListener(apply);
    };
  }, []);

  // Accessibility — a customer who explicitly requested reduced motion
  // receives the same polished static poster fallback on every viewport.
  // Standard browser matchMedia; no third-party library.
  // Lazy initializer prevents a brief <video> mount on the first render
  // for reduced-motion visitors — critical so autoplay never even
  // starts on iOS/Safari when the OS setting says "reduce".
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setPrefersReducedMotion(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener("change", apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", apply) : mq.removeListener(apply);
    };
  }, []);

  // onError fallback — if the browser fails to load any of the sources
  // we swap back to the static image. Tracked in state so a single failed
  // request doesn't leave a broken <video> in the DOM.
  const [videoBroken, setVideoBroken] = useState(false);
  useEffect(() => {
    // Reset if the admin swaps in a new source
    setVideoBroken(false);
  }, [videoMp4, videoWebm]);

  const canPlayVideo = videoEnabled && !videoBroken && !prefersReducedMotion;
  const showDesktopVideo = isDesktop && canPlayVideo;
  const showMobileVideo = !isDesktop && canPlayVideo;

  return (
    <section
      data-testid="hero-section"
      className="relative bg-[#0F0F0F] overflow-hidden"
    >
      {/* ================================================== */}
      {/* MOBILE — stacked: image on top, text below on solid  */}
      {/* ================================================== */}
      <div className="lg:hidden">
        {(showMobileVideo || imgMobile) && (
          <div data-testid="hero-mobile-image" className="relative w-full">
            {/* The mobile hero art file (1080×1350) carries a deliberate
                dark bottom band designed to blend into a taller layout.
                On mobile we crop to a 4/3 container with
                `object-position: top` so only the meaningful laptop /
                product-scene portion (top ~60%) is rendered — the black
                band is intentionally cropped out to prevent a giant
                empty gap between the artwork and the hero copy below.
                The same 4/3 container hosts the CMS-managed hero video
                when enabled — mobile now runs the SAME animation as
                desktop, reading the exact same
                `hero_video_enabled` / `hero_video_mp4` / `hero_video_webm`
                Dashboard fields. Static poster (`hero_image_mobile`)
                remains the fallback when the video can't play or
                reduced motion is requested — but we intentionally do
                NOT hand it to the video's `poster` attribute so the
                animation never has to visibly "replace" a different
                still frame on first paint. `preload="auto"` lets the
                browser fetch the first few segments eagerly so playback
                starts as soon as the DOM is ready. */}
            {showMobileVideo ? (
              <video
                data-testid="hero-mobile-video"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster={imgMobile || undefined}
                aria-label={imgAlt}
                onError={() => setVideoBroken(true)}
                className="block w-full h-auto object-cover object-top bg-[#0F0F0F]"
                style={{ aspectRatio: "4/3" }}
              >
                {videoWebm && <source src={videoWebm} type="video/webm" />}
                <source src={videoMp4} type="video/mp4" />
              </video>
            ) : (
              <img
                src={imgMobile}
                alt={imgAlt}
                className="block w-full h-auto object-cover object-top"
                style={{ aspectRatio: "4/3" }}
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
            )}
            {/* Bottom fade (h-24) — the eyebrow chip and enlarged
                headline overlap the image's lower edge (~88px pull), so
                the gradient covers that full band for readability while
                blending into the dark content block below. */}
            <div
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
              style={{ background: "linear-gradient(to bottom, transparent 0%, rgba(15,15,15,0.55) 45%, #0F0F0F 100%)" }}
            />
          </div>
        )}
        <div className="bg-[#0F0F0F] px-5 pt-2 pb-14">
          {/* The pull-up lives on a BLOCK wrapper — .ur-eyebrow is
              inline-flex, and vertical margins are ignored on
              inline-level boxes. */}
          <div className="relative z-10 -mt-[88px] mb-4">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="ur-eyebrow bg-[#0F0F0F]"
            >
              {eyebrow}
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="relative z-10 font-display text-[clamp(46px,15vw,60px)] font-black leading-[0.95] tracking-[-0.02em] text-white"
          >
            {line1} <span className="text-[#FF6600]">{line2}</span>
          </motion.div>
          {heroShowDot && <div className="mt-3 flex"><span className="w-2 h-2 rounded-full bg-[#FF6600]" data-testid="hero-dot-mobile" /></div>}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="mt-4 text-[#B5B5B5] text-[15px] leading-relaxed max-w-md"
          >
            {description}
          </motion.p>

          <div data-testid="hero-proof-points-mobile" className="mt-6 sm:mt-8 grid grid-cols-3 gap-3 sm:gap-6">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.05 }}
                className="flex flex-col"
              >
                {s.icon && <StatIcon name={s.icon} className="w-6 h-6 sm:w-9 sm:h-9 text-[#FF6600] mb-2 sm:mb-3" />}
                <div className="border-t border-[#333] pt-3 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em] sm:tracking-[0.18em] leading-snug text-white">{s.title}</div>
                <div className="text-[11px] sm:text-xs text-[#A3A3A3] mt-1 leading-snug">{s.sub}</div>
              </motion.div>
            ))}
          </div>

          <div className="mt-9 sm:mt-8 flex flex-col gap-3">
            <InlineLink to={ctaPrimary.link} testid="hero-cta-build-mobile" className="ur-btn-primary w-full">
              {ctaPrimary.label} <ArrowRight className="w-4 h-4" />
            </InlineLink>
            <InlineLink to={ctaSecondary.link} testid="hero-cta-laptops-mobile" className="ur-btn-secondary w-full">
              {ctaSecondary.label} <ArrowRight className="w-4 h-4" />
            </InlineLink>
          </div>
        </div>
      </div>

      {/* ================================================== */}
      {/* DESKTOP — text left (contained), image bleeds right */}
      {/* ================================================== */}
      <div className="hidden lg:block relative">
        <div
          aria-hidden
          className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full opacity-20 blur-[120px] pointer-events-none ur-ambient-drift-a"
          style={{ background: "radial-gradient(circle at 30% 30%, #FF6600 0%, transparent 60%)" }}
        />
        <div
          aria-hidden
          className="absolute top-40 right-0 w-[700px] h-[700px] rounded-full opacity-25 blur-[140px] pointer-events-none ur-ambient-drift-b"
          style={{ background: "radial-gradient(circle, #FF8A33 0%, transparent 60%)" }}
        />

        <div className="relative min-h-[620px] xl:min-h-[660px] min-[1440px]:min-h-[clamp(660px,50vh,780px)] flex items-center">
          {/* Image — absolute, right-aligned bleed. Slightly narrower so left side (coffee mug) escapes text overlap zone.
              Feb 2026 performance pass: framer-motion opacity/scale
              intro removed. That transition was a JS-gated 1.1 s
              fade-in (delay 0.2 s + duration 0.9 s) that made the
              hero appear late regardless of how fast the asset
              itself loaded. The hero now paints as soon as the
              browser has the poster / first video frame.
              Sep 2026 wide-desktop pass: the visual absorbs the extra
              width — 66% → 68% progressively at 1440px+/1536px+ while
              the text column grows only slightly (below). Proportions,
              object-fit/position and both scrims untouched, so the
              approved laptop composition is pixel-preserved. */}
          <div
            data-testid="hero-image-wrapper"
            className="absolute inset-y-0 right-0 w-[66%] xl:w-[64%] min-[1440px]:w-[66%] 2xl:w-[68%] overflow-hidden"
          >
            {showDesktopVideo ? (
              <video
                data-testid="hero-desktop-video"
                autoPlay
                muted
                loop
                playsInline
                poster={imgDesktop || undefined}
                aria-label={imgAlt}
                onError={() => setVideoBroken(true)}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: "50% 8%" }}
              >
                {videoWebm && <source src={videoWebm} type="video/webm" />}
                <source src={videoMp4} type="video/mp4" />
              </video>
            ) : (
              <img
                data-testid="hero-desktop-image"
                src={imgDesktop}
                alt={imgAlt}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: "50% 8%" }}
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
            )}
            {/* dark scrim on image's left edge — narrower now to match the slimmer image; still blends into text side */}
            <div
              aria-hidden
              className="absolute inset-y-0 left-0 w-[42%] pointer-events-none"
              style={{ background: "linear-gradient(to right, #0F0F0F 0%, rgba(15,15,15,0.85) 45%, rgba(15,15,15,0.35) 80%, transparent 100%)" }}
            />
            {/* subtle bottom fade for smooth transition into next section */}
            <div
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
              style={{ background: "linear-gradient(to bottom, transparent 0%, #0F0F0F 100%)" }}
            />
          </div>

          {/* Text — overlays on top of image, vertically centered via parent flex items-center.
              Sep 2026 wide-desktop pass: the content shell expands past
              the 1400px laptop cap (→ 1560px max, fluid clamp() gutters)
              so the composition uses wide monitors instead of floating a
              laptop-sized layout inside them. Text column relaxes
              680 → 720px only; typography sizes untouched. Below 1440px
              every value is identical to the approved composition. */}
          <div className="relative z-10 w-full max-w-[1400px] min-[1440px]:max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 min-[1440px]:px-[clamp(2.5rem,4vw,4rem)]">
            <div className="flex flex-col pt-10 pb-20 max-w-[680px] min-[1440px]:max-w-[880px]">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="ur-eyebrow">
                {eyebrow}
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.05 }}
                className="font-display mt-4 text-5xl lg:text-[72px] xl:text-[80px] min-[1440px]:text-[clamp(96px,7vw,108px)] font-black leading-[1.02] tracking-tight"
              >
                {line1}
                {heroAccentInline
                  ? <span className="text-[#FF6600]">{line2}</span>
                  : <><br /><span className="text-[#FF6600]">{line2}</span></>}
              </motion.h1>
              {heroShowDot && <div className="mt-4 flex"><span className="w-2 h-2 rounded-full bg-[#FF6600]" data-testid="hero-dot-desktop" /></div>}
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.12 }}
                className="mt-5 text-[#B5B5B5] text-lg max-w-xl leading-relaxed"
              >
                {description}
              </motion.p>

              <div data-testid="hero-proof-points" className="mt-8 grid grid-cols-3 gap-6 max-w-xl">
                {stats.map((s, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + i * 0.06 }}
                    className="flex flex-col"
                  >
                    {s.icon && <StatIcon name={s.icon} className="w-10 h-10 text-[#FF6600] mb-3" />}
                    <div className="border-t border-[#333] pt-3 text-[11px] font-bold uppercase tracking-[0.18em] leading-snug text-white">{s.title}</div>
                    <div className="text-xs text-[#A3A3A3] mt-1">{s.sub}</div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <InlineLink to={ctaPrimary.link} testid="hero-cta-build" className="ur-btn-primary">
                  {ctaPrimary.label} <ArrowRight className="w-4 h-4" />
                </InlineLink>
                <InlineLink to={ctaSecondary.link} testid="hero-cta-laptops" className="ur-btn-secondary">
                  {ctaSecondary.label} <ArrowRight className="w-4 h-4" />
                </InlineLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
